import { Bus, Command, Fuel, Server } from "lucide-react";
import {
  ExecutiveAlertItem,
  ExecutiveGoalItem,
  ScorecardItem,
  RiskMatrixItem,
  ExecutiveRole,
  CrossModuleDomain,
} from "./types";

export const MOCK_EXECUTIVE_ALERTS: ExecutiveAlertItem[] = [
  {
    id: "alt-att-01",
    title: "High Attendance Risk Alert",
    category: "Attendance",
    severity: "critical",
    count: 142,
    module: "Attendance",
    timestamp: "10 mins ago",
    drillDownRoute: "/reports?id=rep-att-01",
    reportId: "rep-att-01",
  },
  {
    id: "alt-fee-01",
    title: "Pending Quarter Fee Defaulters",
    category: "Finance",
    severity: "critical",
    count: 215,
    module: "Fees",
    timestamp: "25 mins ago",
    drillDownRoute: "/reports?id=rep-fee-01",
    reportId: "rep-fee-01",
  },
  {
    id: "alt-pay-01",
    title: "Monthly Payroll Approval Pending",
    category: "Payroll",
    severity: "warning",
    count: 1,
    module: "Payroll",
    timestamp: "1 hour ago",
    drillDownRoute: "/reports?id=rep-pay-01",
    reportId: "rep-pay-01",
  },
  {
    id: "alt-inv-01",
    title: "Lab Chemical Stock Below Reorder Threshold",
    category: "Inventory",
    severity: "warning",
    count: 18,
    module: "Inventory",
    timestamp: "2 hours ago",
    drillDownRoute: "/reports?id=rep-inv-01",
    reportId: "rep-inv-01",
  },
  {
    id: "alt-ast-01",
    title: "Campus Server AMC Maintenance Due",
    category: "Assets",
    severity: "warning",
    count: 6,
    module: "Assets",
    timestamp: "3 hours ago",
    drillDownRoute: "/reports?id=rep-ast-01",
    reportId: "rep-ast-01",
  },
  {
    id: "alt-tr-01",
    title: "Bus Route 14 Fuel Consumption Spike (+24%)",
    category: "Transport",
    severity: "info",
    count: 2,
    module: "Transport",
    timestamp: "4 hours ago",
    drillDownRoute: "/reports?id=rep-tr-01",
    reportId: "rep-tr-01",
  },
  {
    id: "alt-plc-01",
    title: "Placement Target Shortfall in Civil Dept",
    category: "Placement",
    severity: "warning",
    count: 35,
    module: "Placement",
    timestamp: "5 hours ago",
    drillDownRoute: "/reports?id=rep-plc-01",
    reportId: "rep-plc-01",
  },
  {
    id: "alt-vis-01",
    title: "Unresolved Gate Pass Expiry Alerts",
    category: "Visitor Security",
    severity: "info",
    count: 4,
    module: "Visitor",
    timestamp: "6 hours ago",
    drillDownRoute: "/reports?id=rep-vis-01",
    reportId: "rep-vis-01",
  },
  {
    id: "alt-ai-01",
    title: "AI Token Consumption Reached 85% Monthly Cap",
    category: "AI Copilot",
    severity: "warning",
    count: 1,
    module: "AI",
    timestamp: "Just now",
    drillDownRoute: "/reports?id=rep-ai-01",
    reportId: "rep-ai-01",
  },
];

export const MOCK_EXECUTIVE_GOALS: ExecutiveGoalItem[] = [
  {
    id: "goal-01",
    title: "Tuition Fee Revenue Target",
    category: "Financials",
    targetValue: "₹15.0Cr",
    actualValue: "₹14.2Cr",
    unit: "INR",
    completionPercent: 94.6,
    monthlyProgress: 96.2,
    annualProgress: 94.6,
    department: "Finance",
  },
  {
    id: "goal-02",
    title: "Campus Placement Rate",
    category: "Career Placement",
    targetValue: "92.0%",
    actualValue: "89.4%",
    unit: "%",
    completionPercent: 97.1,
    monthlyProgress: 98.0,
    annualProgress: 97.1,
    department: "Placement Office",
  },
  {
    id: "goal-03",
    title: "Faculty Scopus Publications",
    category: "Research Impact",
    targetValue: 150,
    actualValue: 142,
    unit: "Papers",
    completionPercent: 94.6,
    monthlyProgress: 92.0,
    annualProgress: 94.6,
    department: "Academics & Research",
  },
  {
    id: "goal-04",
    title: "Minimum Student Attendance",
    category: "Academic Compliance",
    targetValue: "90.0%",
    actualValue: "91.8%",
    unit: "%",
    completionPercent: 102.0,
    monthlyProgress: 101.5,
    annualProgress: 102.0,
    department: "Academic Dean",
  },
];

export const MOCK_SCORECARDS: ScorecardItem[] = [
  { id: "sc-01", metricName: "Student Admission Conversion", actual: "92.4%", target: "90.0%", variance: +2.4, status: "excellent", department: "Admissions" },
  { id: "sc-02", metricName: "Semester Pass Rate", actual: "88.6%", target: "85.0%", variance: +3.6, status: "good", department: "Academics" },
  { id: "sc-03", metricName: "Fee Collection Efficiency", actual: "94.2%", target: "95.0%", variance: -0.8, status: "good", department: "Finance" },
  { id: "sc-04", metricName: "Faculty Retention Rate", actual: "96.5%", target: "95.0%", variance: +1.5, status: "excellent", department: "HR" },
  { id: "sc-05", metricName: "Lab Asset AMC Compliance", actual: "78.0%", target: "90.0%", variance: -12.0, status: "critical", department: "Assets" },
];

export const MOCK_RISK_MATRIX: RiskMatrixItem[] = [
  { id: "risk-01", title: "Fee Collection Shortfall in Sem 4", impact: "high", probability: "medium", category: "Finance", riskScore: 82 },
  { id: "risk-02", title: "Faculty Shortage in AI Department", impact: "high", probability: "high", category: "HR", riskScore: 91 },
  { id: "risk-03", title: "Transport Bus Maintenance Delay", impact: "medium", probability: "medium", category: "Operations", riskScore: 64 },
  { id: "risk-04", title: "AI API Token Exceeding Monthly Budget", impact: "medium", probability: "high", category: "AI System", riskScore: 75 },
];

export const MOCK_ROLE_TITLES: Record<ExecutiveRole, { title: string; focus: string }> = {
  "Super Admin": { title: "Super Admin Command Dashboard", focus: "Full Institutional Control & System Governance" },
  Principal: { title: "Principal Leadership Dashboard", focus: "Academic Excellence, Accreditation & Strategic Growth" },
  "Vice Principal": { title: "Vice Principal Operations Dashboard", focus: "Daily Operations, Timetables & Faculty Coordination" },
  Registrar: { title: "Registrar Administrative Dashboard", focus: "Student Records, Compliance & University Affiliations" },
  HOD: { title: "Head of Department (HOD) Dashboard", focus: "Departmental Performance, Curriculum & Faculty Loads" },
  "Finance Officer": { title: "Chief Financial Officer (CFO) Dashboard", focus: "Tuition Revenues, Fee Recovery, Payroll & Budgets" },
  "HR Manager": { title: "Human Resources Director Dashboard", focus: "Staff Recruitment, Appraisals, Leaves & Payroll" },
  "Library Admin": { title: "Chief Librarian Dashboard", focus: "Book Circulation, Digital Journals & Overdue Fines" },
  "Transport Manager": { title: "Transport Director Dashboard", focus: "Fleet Management, Bus Routes, Fuel & Driver Logs" },
  "Hostel Warden": { title: "Chief Hostel Warden Dashboard", focus: "Room Allocations, Mess Operations & Security" },
  "Placement Officer": { title: "Head of Placements Dashboard", focus: "Corporate Campus Drives, Student CTC & Internships" },
};
